function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }
function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }
import React, { useState, useContext, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import Carousel from '../carousel/v2';
import { ProductProviderContext } from '../product-provider';
import ProductTileWithSwatches from '../product-tile-with-swatches';
import Button from '../button';
import LoadingIndicator from '../loading-indicator';
import { BREAKPOINTS } from '../../utils/breakpoints';
import { useBreakpoint } from '../../utils/hooks';
import CarouselButton from '../carousel/common/carousel-button';
require("./product-carousel.css");
var styles = {
  "container": "container-5GoeS",
  "leftArrow": "leftArrow-U3U3e",
  "rightArrow": "rightArrow-2q4aB",
  "title": "title-2NX-A",
  "productTile": "productTile-3HkaW",
  "nonCarouselContainer": "nonCarouselContainer-2fXTJ",
  "ctaButton": "ctaButton-2h8YZ"
};
var ProductCarousel = _ref => {
  var {
    title,
    className,
    productIds,
    children,
    callToAction,
    webRoute,
    tilesPerPage,
    onSlideIndexChange,
    onSwatchClick,
    onProductClick
  } = _ref;
  var [productTilesProps, setProductTilesProps] = useState([]);
  var [isLoading, setIsLoading] = useState(false);
  var [arrowTopOffset, setArrowTopOffset] = useState(0);
  var productImgRef = useRef();
  var {
    getProductTilePropsByIds
  } = useContext(ProductProviderContext);
  var breakpoint = useBreakpoint();
  var isMobile = breakpoint === BREAKPOINTS.MOBILE;
  var slidesPerPage = isMobile ? 2 : tilesPerPage;
  var setArrowTopOffsetFromRef = useCallback(() => {
    if (productImgRef.current) {
      var _productImgRef$curren;
      var imageHeight = Math.round(((_productImgRef$curren = productImgRef.current) === null || _productImgRef$curren === void 0 ? void 0 : _productImgRef$curren.getBoundingClientRect().height) || 0);
      var newArrowTopOffset = imageHeight / 2;
      if (newArrowTopOffset !== arrowTopOffset) {
        setArrowTopOffset(newArrowTopOffset);
      }
    }
  }, [arrowTopOffset]);
  var productImgCallbackRef = useCallback(node => {
    if (node && productImgRef.current !== node) {
      productImgRef.current = node;
      setArrowTopOffsetFromRef();
    }
  }, [setArrowTopOffsetFromRef]);
  useEffect(() => {
    if (children) {
      // ignore any data fetching if children are provided
      return;
    }
    if (!getProductTilePropsByIds) {
      // eslint-disable-next-line no-console
      console.error("Missing children prop or implementation of 'getProductTilePropsByIds' function in 'ProductProvider' context.");
      return;
    }
    setIsLoading(true);
    function initializeProductTilesProps() {
      return _initializeProductTilesProps.apply(this, arguments);
    }
    function _initializeProductTilesProps() {
      _initializeProductTilesProps = _asyncToGenerator(function* () {
        var productData = yield getProductTilePropsByIds(productIds);
        setProductTilesProps(productData);
        setIsLoading(false);
      });
      return _initializeProductTilesProps.apply(this, arguments);
    }
    initializeProductTilesProps();
  }, [productIds, getProductTilePropsByIds, children]);
  var handleResize = useCallback(() => {
    setArrowTopOffsetFromRef();
  }, [setArrowTopOffsetFromRef]);
  useLayoutEffect(() => {
    if (window !== undefined) {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
    return null;
  }, [handleResize]);
  var productTiles = useMemo(() => {
    return children ? React.Children.map(children, (child, index) => /*#__PURE__*/React.cloneElement(child, {
      className: cs(child.props.className, styles.productTile),
      imageRef: index === 0 ? productImgCallbackRef : undefined,
      onSwatchClick: onSwatchClick,
      onProductClick: onProductClick
    })) : productTilesProps.map((productTileProps, index) => /*#__PURE__*/React.createElement(ProductTileWithSwatches, _extends({}, productTileProps, {
      className: cs(productTileProps.className, styles.productTile),
      imageRef: index === 0 ? productImgCallbackRef : undefined
      // eslint-disable-next-line react/no-array-index-key
      ,
      key: index,
      onSwatchClick: onSwatchClick,
      onProductClick: onProductClick,
      totalProductTileCount: productTilesProps === null || productTilesProps === void 0 ? void 0 : productTilesProps.length
    })));
  }, [children, onSwatchClick, onProductClick, productTilesProps, productImgCallbackRef]);
  if (isLoading) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.container
    }, /*#__PURE__*/React.createElement(LoadingIndicator, null));
  }
  if (!children && !(productTilesProps !== null && productTilesProps !== void 0 && productTilesProps.length)) {
    return null;
  }
  var disableCarousel = productTiles.length <= slidesPerPage;
  return /*#__PURE__*/React.createElement("div", {
    className: cs(styles.container, className)
  }, !!title && /*#__PURE__*/React.createElement("h2", {
    className: cs(styles.title, 'lll-text-large')
  }, title), /*#__PURE__*/React.createElement(Carousel, {
    classes: {
      carousel: cs(styles.carousel, {
        [styles.nonCarouselContainer]: disableCarousel
      })
    },
    customLeftArrow: /*#__PURE__*/React.createElement(CarouselButton, {
      className: styles.leftArrow,
      direction: "left",
      style: {
        top: "".concat(arrowTopOffset, "px")
      }
    }),
    customRightArrow: /*#__PURE__*/React.createElement(CarouselButton, {
      className: styles.rightArrow,
      direction: "right",
      style: {
        top: "".concat(arrowTopOffset, "px")
      }
    }),
    infinite: !isMobile && !disableCarousel,
    onSlideIndexChange: onSlideIndexChange,
    paginationLocation: "bottom",
    showArrows: !isMobile && !disableCarousel,
    showPagination: !disableCarousel,
    slidesPerPage: slidesPerPage,
    v2: true
  }, productTiles), callToAction && /*#__PURE__*/React.createElement(Button, {
    className: styles.ctaButton,
    href: webRoute,
    mobileBlock: true
  }, callToAction));
};
ProductCarousel.propTypes = {
  /** Title shown above the carousel. Renders as an h2 element */
  title: PropTypes.string,
  /** Class to add to the Product Carousel container */
  className: PropTypes.string,
  /** Array of product ids, if provided will use the getProductTilePropsByIds function from the ProductProvider context */
  productIds: PropTypes.arrayOf(PropTypes.string),
  /** ProductTiles to render inside the carousel */
  children: PropTypes.node,
  /** Call to action text to render inside the CTA button */
  callToAction: PropTypes.string,
  /** Href assigned to the CTA button */
  webRoute: PropTypes.string,
  /** The number of ProductTiles to be displayed per page (on desktop). Mobile defaults to 2. */
  tilesPerPage: PropTypes.number,
  onSlideIndexChange: PropTypes.func,
  onSwatchClick: PropTypes.func,
  onProductClick: PropTypes.func
};
ProductCarousel.defaultProps = {
  title: '',
  className: '',
  productIds: [],
  children: undefined,
  callToAction: '',
  webRoute: '',
  tilesPerPage: 4,
  onSlideIndexChange: () => {},
  onSwatchClick: () => {},
  onProductClick: () => {}
};
export default ProductCarousel;